import React from 'react';

import { DeleteIcon } from '@vlabs/icons';
import { DeleteListsForm } from '@vlabs/monokit/entities/lists';
import { useModal } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button';
import { useTranslation } from 'react-i18next';

import { useDeleteLists } from './useDeleteLists';

interface DeleteListsButtonProps {
  listIds: string[];
  onDeleteLists?: VoidFunction;
  enabled?: boolean;
  'data-testid'?: string;
  size?: 'small' | 'medium';
  variant?: string;
  kind?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}

const displayName = 'DeleteListsButton';

function DeleteListsButton({
  listIds,
  onDeleteLists = undefined,
  enabled = true,
  'data-testid': dataTestId = 'DeleteListsButton',
  children = undefined,
  ...props
}: DeleteListsButtonProps): React.JSX.Element {
  const { t } = useTranslation();
  const modal = useModal();
  const deleteLists = useDeleteLists();

  return (
    <React.Fragment>
      {modal.wrap(
        <DeleteListsForm
          listIds={listIds}
          onCancel={modal.close}
          onSubmit={async (values) => {
            await deleteLists.mutateAsync(values);
            onDeleteLists?.();
            modal.close();
          }}
        />,
      )}

      <Button
        data-testid={dataTestId}
        disabled={!enabled}
        icon={<DeleteIcon />}
        kind="negative"
        onClick={modal.open}
        variant="flat"
        {...props}
      >
        {children !== undefined ? children : t('features:delete_lists.button')}
      </Button>
    </React.Fragment>
  );
}

DeleteListsButton.displayName = displayName;

export { DeleteListsButton };
